import React, { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import Modal from "../common/Modal";
import Input from "../common/Input";
import Button from "../common/Button";
import { ReportFormat } from "../../schemas/reportSchema";

const emailReportSchema = z.object({
  email: z.string().email("Invalid email address").min(1, "Email is required"),
});

type EmailReportFormData = z.infer<typeof emailReportSchema>;

interface EmailReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (email: string, format: ReportFormat) => Promise<void>;
  isLoading?: boolean;
  reportName?: string;
  defaultFormat?: ReportFormat;
}

const EmailReportModal: React.FC<EmailReportModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
  reportName = "Report",
  defaultFormat = "PDF",
}) => {
  const [selectedFormat, setSelectedFormat] = useState<ReportFormat>(defaultFormat);

  const {
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    reset,
    trigger,
  } = useForm<EmailReportFormData>({
    resolver: zodResolver(emailReportSchema),
    mode: "onBlur",
    reValidateMode: "onBlur",
    defaultValues: { email: "" },
  });

  const prevIsOpenRef = useRef(false);

  useEffect(() => {
    if (isOpen && !prevIsOpenRef.current) {
      reset({ email: "" });
      setSelectedFormat(defaultFormat);
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen, reset, defaultFormat]);

  const handleFormSubmit = async (data: EmailReportFormData) => {
    await onSubmit(data.email, selectedFormat);
    reset({ email: "" });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Send ${reportName} via Email`}
      size="md"
    >
      <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6" noValidate>
        <div>
          <p className="text-sm text-gray-600 mb-4">
            Enter the email address where you want to send the report.
          </p>
          <Input
            label="Email Address"
            placeholder="Enter email address"
            name="email"
            type="email"
            value={watch("email") || ""}
            onChange={(e) => setValue("email", e.target.value, { shouldValidate: false })}
            onBlur={() => trigger("email")}
            error={errors.email}
            autoComplete="email"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Report Format
          </label>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setSelectedFormat("PDF")}
              className={`flex-1 px-4 py-2 text-sm font-medium rounded-lg border-2 transition-colors ${
                selectedFormat === "PDF"
                  ? "border-primary-default bg-primary-default text-white"
                  : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
              }`}
            >
              PDF
            </button>
            <button
              type="button"
              onClick={() => setSelectedFormat("EXCEL")}
              className={`flex-1 px-4 py-2 text-sm font-medium rounded-lg border-2 transition-colors ${
                selectedFormat === "EXCEL"
                  ? "border-primary-default bg-primary-default text-white"
                  : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
              }`}
            >
              Excel
            </button>
          </div>
        </div>

        <div className="flex justify-end space-x-4 pt-4">
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button type="submit" variant="primary" isLoading={isLoading}>
            Send Email
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default EmailReportModal;
